import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Textarea,
  Input,
  Select,
  Option,
  IconButton,
  Tooltip,
} from '@material-tailwind/react';
import {
  TrashIcon,
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

// Derive paragraph blocks from content, supporting the legacy single-body shape
function blocksFromContent(content) {
  if (content?.sections?.length) return content.sections;
  if (content?.body) return [{ heading: '', content: content.body }];
  return [{ heading: '', content: '' }];
}

/**
 * Editor for a user-created custom section: free-text title, a dynamic list of
 * paragraph blocks (optional sub-heading + text, add/remove/reorder), and a
 * placement selector (which built-in section it renders after).
 */
function CustomSectionEditor({
  content,
  placementOptions, // [{ id, label }] built-in sections of this report type
  onSave,
  onDelete,
  isSaving,
}) {
  const [title, setTitle] = useState(content?.title || '');
  const [blocks, setBlocks] = useState(blocksFromContent(content));
  const [insertAfter, setInsertAfter] = useState(content?.insertAfter || '');
  const [isDirty, setIsDirty] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Sync when switching between sections or after external updates
  useEffect(() => {
    setTitle(content?.title || '');
    setBlocks(blocksFromContent(content));
    setInsertAfter(content?.insertAfter || '');
    setIsDirty(false);
    setConfirmingDelete(false);
  }, [content]);

  const handleBlockChange = (index, field, value) => {
    setBlocks((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setIsDirty(true);
  };

  const addBlock = () => {
    setBlocks((prev) => [...prev, { heading: '', content: '' }]);
    setIsDirty(true);
  };

  const removeBlock = (index) => {
    setBlocks((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const moveBlock = (index, direction) => {
    setBlocks((prev) => {
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const updated = [...prev];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated;
    });
    setIsDirty(true);
  };

  const handleSave = () => {
    onSave({
      title: title.trim() || 'Custom Section',
      sections: blocks.filter((b) => (b.heading || '').trim() || (b.content || '').trim()),
      body: '', // Clear legacy single-body field
      ...(insertAfter ? { insertAfter } : {}),
    });
    setIsDirty(false);
  };

  return (
    <Card className="h-full flex flex-col">
      <CardBody className="flex-1 flex flex-col p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <Typography variant="h6" color="blue-gray">
            Custom Section
          </Typography>
          {confirmingDelete ? (
            <div className="flex items-center gap-2">
              <Typography variant="small" color="red" className="font-medium">
                Delete this section?
              </Typography>
              <Button size="sm" color="red" onClick={onDelete} disabled={isSaving}>
                Delete
              </Button>
              <Button size="sm" variant="outlined" onClick={() => setConfirmingDelete(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Tooltip content="Delete section">
              <IconButton
                variant="text"
                size="sm"
                color="red"
                onClick={() => setConfirmingDelete(true)}
              >
                <TrashIcon className="h-4 w-4" />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <div className="flex-1 overflow-y-auto space-y-4">
          <div className="space-y-1">
            <Typography variant="small" className="font-medium text-gray-700">
              Title
            </Typography>
            <Input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setIsDirty(true);
              }}
              placeholder="e.g. Maintenance Highlights"
            />
          </div>

          {placementOptions?.length > 0 && (
            <div className="space-y-1">
              <Typography variant="small" className="font-medium text-gray-700">
                Placement
              </Typography>
              <Select
                value={insertAfter}
                onChange={(value) => {
                  setInsertAfter(value);
                  setIsDirty(true);
                }}
                label="Show after"
              >
                {placementOptions.map((opt) => (
                  <Option key={opt.id} value={opt.id}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          {/* Dynamic paragraph blocks */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Typography variant="small" className="font-medium text-gray-700">
                Paragraphs
              </Typography>
              <Button
                variant="text"
                size="sm"
                className="flex items-center gap-1"
                onClick={addBlock}
              >
                <PlusIcon className="h-4 w-4" />
                Add Paragraph
              </Button>
            </div>

            {blocks.map((block, index) => (
              <div
                key={index}
                className="border rounded-lg p-3 space-y-2 bg-gray-50"
              >
                <div className="flex items-center justify-between">
                  <Typography variant="small" className="font-medium text-gray-600">
                    Paragraph {index + 1}
                  </Typography>
                  <div className="flex items-center gap-1">
                    <Tooltip content="Move up">
                      <IconButton
                        variant="text"
                        size="sm"
                        onClick={() => moveBlock(index, -1)}
                        disabled={index === 0}
                      >
                        <ChevronUpIcon className="h-4 w-4" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content="Move down">
                      <IconButton
                        variant="text"
                        size="sm"
                        onClick={() => moveBlock(index, 1)}
                        disabled={index === blocks.length - 1}
                      >
                        <ChevronDownIcon className="h-4 w-4" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content="Remove paragraph">
                      <IconButton
                        variant="text"
                        size="sm"
                        color="red"
                        onClick={() => removeBlock(index)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                <Input
                  label="Heading (optional)"
                  value={block.heading || ''}
                  onChange={(e) => handleBlockChange(index, 'heading', e.target.value)}
                />
                <Textarea
                  label="Text"
                  value={block.content || ''}
                  onChange={(e) => handleBlockChange(index, 'content', e.target.value)}
                  rows={5}
                />
              </div>
            ))}

            {blocks.length === 0 && (
              <div className="text-center py-4 text-gray-500 border rounded-lg border-dashed">
                <Typography variant="small">No paragraphs yet. Click "Add Paragraph" to create one.</Typography>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex-shrink-0">
          <Button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            loading={isSaving}
            fullWidth
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default CustomSectionEditor;
